import { useState } from 'react';
import { useCreateDmDoctor } from '../../../../../hooks/usePracticeDm';
import { useIdempotencyKey } from '../../../../../hooks/useIdempotencyKey';
import type { DmDoctorOption, DmPracticeOption } from '../../../../../services/practiceDm';
import { buildDmDoctorDraft, validateDmDoctorForm, type DmDoctorForm } from './dmForms.ts';
import { dmErrorMessage } from './dmErrorMessage';
import type { DmLinkSave } from './useDmLinkSave';

// Once the doctor exists in DM, a retry only re-runs the save, never a second create.
export function useDmDoctorStep(practiceId: string, initialForm: DmDoctorForm, save: DmLinkSave) {
    const [form, setForm] = useState(initialForm);
    const [practices, setPractices] = useState<DmPracticeOption[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const [created, setCreated] = useState<DmDoctorOption | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const create = useCreateDmDoctor(practiceId);
    const keyFor = useIdempotencyKey();

    const createDoctor = async (): Promise<DmDoctorOption | null> => {
        const practiceIds = practices.map((practice) => practice.practiceId);
        const draft = buildDmDoctorDraft(form, practiceIds);
        try {
            const doctor = await create.mutateAsync({
                ...draft,
                idempotencyKey: keyFor(JSON.stringify(draft))
            });
            setCreated(doctor);
            return doctor;
        } catch (error) {
            setSubmitError(dmErrorMessage(error, 'Could not create the DM doctor.'));
            return null;
        }
    };

    // DM omits `practices` on a fresh doctor, so the link uses the main practice we sent.
    const submit = async () => {
        const main = practices[0];
        if (!created && Object.keys(validateDmDoctorForm(form)).length > 0) {
            setShowErrors(true);
            return;
        }
        setSubmitError(null);
        const doctor = created ?? (await createDoctor());
        if (doctor) await save(doctor.doctorId, main.practiceId);
    };

    return {
        form,
        setForm,
        practices,
        setPractices,
        errors: showErrors ? validateDmDoctorForm(form) : {},
        created,
        submitError,
        submit: () => void submit(),
        isCreating: create.isPending
    };
}

export type DmDoctorStep = ReturnType<typeof useDmDoctorStep>;
